import { RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ROLE } from '@/common/role';
import { Button } from '@/components/ui/button';
import { useKeycloak } from '@/context/KeycloakContext';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { cn } from '@/lib/utils';
import type { SoftDeleteTarget } from '@/queries/soft-delete/soft-delete';

import { RestoreConfirmDialog } from './RestoreConfirmDialog';

type RestoreItemButtonProps = {
	target: SoftDeleteTarget;
	/** Shown in the confirmation: "Restore Bridge A?" */
	name: string;
	/** The role that may restore it: the entity's delete role */
	restoreRole: ROLE;
	/** After a restore, e.g. to reopen the page as the live item */
	onRestored: () => void;
	className?: string;
};

/**
 * Restore for a deleted item opened as a read-only preview (GH #331), in the place its Delete button has
 * on the live item. The role is checked on the user's own roles: the preview is read-only, which hides
 * the delete role restore needs from the permission hooks.
 */
export function RestoreItemButton({ target, name, restoreRole, onRestored, className }: RestoreItemButtonProps) {
	const { t } = useTranslation();
	const { roles } = useKeycloak();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const restore = useRestoreItem({
		onRestored: () => {
			setIsConfirmOpen(false);
			onRestored();
		},
	});
	if (!roles.has(restoreRole)) {
		return null;
	}
	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className={cn('bg-white shadow-sm', className)}
				onClick={() => setIsConfirmOpen(true)}
			>
				<RotateCcw />
				{t('common.actions.restore')}
			</Button>
			<RestoreConfirmDialog
				open={isConfirmOpen}
				onOpenChange={setIsConfirmOpen}
				target={target}
				name={name}
				isRestoring={restore.isPending}
				onConfirm={() => restore.mutate({ target, name })}
			/>
		</>
	);
}
